import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../const';
import { readSessionAdmin } from '../utils/userPermissions';

// Deduplicate only in-flight requests; permissions are refreshed on each new visit.
const pendingContexts = new Map();
const requestContext = (token, includeCompanies) => {
  const key = JSON.stringify([token, includeCompanies]);
  if (!pendingContexts.has(key)) {
    const request = axios.get(API_URL + 'auth/context', {
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      ...(includeCompanies ? {} : { params: { include_companies: 0 } }),
    });
    pendingContexts.set(key, request);
    const clear = () => {
      if (pendingContexts.get(key) === request) pendingContexts.delete(key);
    };
    request.then(clear, clear);
  }
  return pendingContexts.get(key);
};

const useAuthenticatedAdmin = (refreshKey, { includeCompanies = true } = {}) => {
  const [admin, setAdmin] = useState(readSessionAdmin);
  const [loading, setLoading] = useState(true);
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');

  useEffect(() => {
    let active = true;
    setLoading(true);
    if (!token) {
      setAdmin(null);
      setLoading(false);
      return () => { active = false; };
    }
    requestContext(token, includeCompanies).then(response => {
      // Ignore an old account's response after an impersonation or login switch.
      if (!active || sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '') !== token) return;
      const context = response.data?.data;
      if (active && response.data?.status && context?.admin) {
        const current = {
          ...readSessionAdmin(), ...context.admin,
          // The context endpoint returns role as a relationship; replace the cached login label.
          role_name: context.admin.role_name ?? context.admin.role?.name ?? '',
          is_vendor_owner: context.admin.is_vendor_owner ?? context.is_vendor_owner ?? false,
          effective_permissions: context.admin.effective_permissions !== undefined
            ? context.admin.effective_permissions : context.admin.permissions,
        };
        setAdmin(current);
        sessionStorage.setItem('quoteadmin', JSON.stringify(current));
      }
    }).catch(() => {
      // The API remains authoritative if refreshing cached session details fails.
    }).finally(() => {
      if (active && sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '') === token) {
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, [token, refreshKey, includeCompanies]);

  return { admin, loading };
};

export default useAuthenticatedAdmin;
